/**
 * FixRunDetailPage - Phase 9: FixRun Detail View
 * 
 * Detail view for a single FixRun with execution results and logs.
 * Phase 9 minimal implementation for navigation testing.
 */

import { useState, useEffect } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { api, FixRunStatusResponse } from '../lib/api'
import { LoadingSpinner } from '../components/LoadingSpinner'
import { StatusBadge } from '../components/StatusBadge'
import { useLocale } from '../contexts/LocaleContext'

export function FixRunDetailPage() {
  const { t } = useLocale()
  const navigate = useNavigate()
  const { fixRunId } = useParams<{ fixRunId: string }>()
  const [searchParams] = useSearchParams()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [data, setData] = useState<FixRunStatusResponse | null>(null)
  
  // Get audit context from URL params if provided
  const auditId = searchParams.get('audit') || undefined
  
  useEffect(() => {
    if (!fixRunId) return
    loadFixRun()
  }, [fixRunId])
  
  async function loadFixRun() {
    if (!fixRunId) return
    
    try {
      setLoading(true)
      setError(null)
      const result = await api.getFixRunStatus(fixRunId)
      setData(result)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('fixRuns.failedLoad'))
    } finally {
      setLoading(false)
    }
  }
  
  if (loading) {
    return <LoadingSpinner />
  }
  
  if (error || !data) {
    return (
      <div className="p-8">
        <div className="bg-red-50 text-red-800 p-4 rounded-lg">
          <p className="font-semibold">{t('fixRuns.errorDetail')}</p>
          <p className="text-sm mt-1">{error || t('fixRuns.notFound')}</p>
          <button
            onClick={() => loadFixRun()}
            className="mt-3 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
          >
            {t('common.retry')}
          </button>
        </div>
      </div>
    )
  }
  
  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('fixRuns.detailTitle')}</h1>
            <p className="text-sm text-gray-600 mt-1 font-mono">
              {data.fix_run_id}
            </p>
          </div>
          <div className="flex gap-3">
            {auditId && (
              <button
                onClick={() => navigate(`/audit/${auditId}/fixruns`)}
                className="px-4 py-2 text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded"
              >
                {t('fixRuns.backHistory')}
              </button>
            )}
            <button
              onClick={() => navigate(`/audit/${data.audit_run_id}`)}
              className="px-4 py-2 text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded"
            >
              {t('fixAi.viewAudit')}
            </button>
          </div>
        </div>
        
        {/* Status Card */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <p className="text-xs text-gray-500 mb-1">{t('fixRuns.colStatus')}</p>
              <StatusBadge status={data.status} />
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">{t('fixRuns.colBranch')}</p>
              <p className="text-sm font-mono truncate" title={data.target_branch}>
                {data.target_branch}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">{t('fixRuns.applied')}</p>
              <p className="text-sm font-semibold text-green-600">
                {data.fixes_applied || 0}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">{t('fixRuns.failedFixes')}</p>
              <p className="text-sm font-semibold text-red-600">
                {data.fixes_failed || 0}
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Details Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('fixRuns.execDetails')}</h2>
        
        <div className="space-y-4">
          <div>
            <p className="text-sm font-medium text-gray-700">{t('fixRuns.baseRef')}</p>
            <p className="text-sm text-gray-900 font-mono mt-1">{data.base_ref}</p>
          </div>
          
          
          {/* Error details would be shown here when available in API */}
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-gray-700">{t('fixRuns.colCreated')}</p>
              <p className="text-sm text-gray-600 mt-1">
                {new Date(data.created_at).toLocaleString()}
              </p>
            </div>
            {data.completed_at && (
              <div>
                <p className="text-sm font-medium text-gray-700">{t('fixRuns.colCompleted')}</p>
                <p className="text-sm text-gray-600 mt-1">
                  {new Date(data.completed_at).toLocaleString()}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Next steps */}
      <div className="mt-6 card">
        <p className="text-sm text-[var(--text-secondary)]">
          {t('fixRuns.nextSteps')}
        </p>
      </div>
    </div>
  )
}
